<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 16,
  },
  perPageOptions: {
    type: Array,
    default: () => [],
  },
  currentPageInfo: {
    type: String,
    default: '',
  },
  hideEdgeNavigationOnMobile: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:currentPage', 'update:itemsPerPage']);
const { t } = useI18n();
const { formatCompactNumber, formatFullNumber } = useNumberFormatter();

const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage)
);
const startItem = computed(
  () => (props.currentPage - 1) * props.itemsPerPage + 1
);
const endItem = computed(() =>
  Math.min(startItem.value + props.itemsPerPage - 1, props.totalItems)
);
const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === totalPages.value);
const changePage = newPage => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    emit('update:currentPage', newPage);
  }
};

const showPerPageSelector = computed(() => props.perPageOptions.length > 0);

const currentPageInformation = computed(() => {
  const translationKey = props.currentPageInfo || 'PAGINATION_FOOTER.SHOWING';
  return t(
    translationKey,
    {
      startItem: formatFullNumber(startItem.value),
      endItem: formatFullNumber(endItem.value),
      totalItems: formatCompactNumber(props.totalItems),
    },
    Number(props.totalItems)
  );
});

const pageInfo = computed(() => {
  return t(
    'PAGINATION_FOOTER.CURRENT_PAGE_INFO',
    {
      currentPage: '',
      totalPages: formatCompactNumber(totalPages.value),
    },
    Number(totalPages.value)
  );
});

const selectPerPage = value => {
  if (Number(value) === props.itemsPerPage) return;
  emit('update:itemsPerPage', Number(value));
};
</script>

<template>
  <div
    class="relative flex flex-col gap-2 sm:flex-row justify-between min-h-[3.375rem] sm:h-[3.375rem] w-full border-t border-n-weak mx-auto bg-n-surface-1 py-2 px-4 sm:py-3 sm:px-6 items-center before:absolute before:inset-x-0 before:-top-4 before:bg-gradient-to-t before:from-n-surface-1 before:from-0% before:to-transparent before:h-4 before:pointer-events-none"
  >
    <div class="flex items-center gap-3 min-w-0">
      <span class="min-w-0 text-body-main line-clamp-1 text-n-slate-11">
        {{ currentPageInformation }}
      </span>
      <div
        v-if="showPerPageSelector"
        class="hidden sm:inline-flex items-center gap-2 shrink-0"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('PAGINATION_FOOTER.PER_PAGE') }}
        </span>
        <div
          class="inline-flex items-center rounded-lg border border-n-weak bg-n-alpha-2 p-0.5"
          role="group"
          :aria-label="t('PAGINATION_FOOTER.PER_PAGE')"
        >
          <button
            v-for="option in perPageOptions"
            :key="option"
            type="button"
            class="min-w-[2.25rem] h-6 px-2 rounded-md text-xs font-medium transition-colors"
            :class="
              Number(option) === itemsPerPage
                ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            :aria-pressed="Number(option) === itemsPerPage"
            @click="selectPerPage(option)"
          >
            {{ option }}
          </button>
        </div>
      </div>
    </div>
    <div
      class="flex items-center gap-2 shrink-0 max-sm:flex-wrap max-sm:justify-center max-sm:max-w-full"
    >
      <Button
        icon="i-lucide-chevrons-left"
        variant="ghost"
        size="sm"
        color="slate"
        class="!w-8 !h-6"
        :class="{ 'max-sm:hidden': hideEdgeNavigationOnMobile }"
        :disabled="isFirstPage"
        @click="changePage(1)"
      />
      <Button
        icon="i-lucide-chevron-left"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :disabled="isFirstPage"
        @click="changePage(currentPage - 1)"
      />
      <div class="inline-flex items-center gap-2 text-sm">
        <span
          class="px-3 tabular-nums py-0.5 font-420 bg-n-input-background text-body-main text-n-slate-12 rounded-md"
        >
          {{ formatFullNumber(currentPage) }}
        </span>
        <span class="truncate text-body-main text-n-slate-11">
          {{ pageInfo }}
        </span>
      </div>
      <Button
        icon="i-lucide-chevron-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :disabled="isLastPage"
        @click="changePage(currentPage + 1)"
      />
      <Button
        icon="i-lucide-chevrons-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :class="{ 'max-sm:hidden': hideEdgeNavigationOnMobile }"
        :disabled="isLastPage"
        @click="changePage(totalPages)"
      />
    </div>
  </div>
</template>
